/**
 * Vista 10 · Derivaciones al Órgano de Control Interno.
 *
 * **Dos causas, dos tonos.** `documento_falso` va en `critical` y
 * `no_acreditado` en `serious`, porque el OCI recibe dos hallazgos de
 * naturaleza distinta: uno dice que alguien presentó un documento falso, el
 * otro que no se pudo comprobar. Pintarlos del mismo rojo —como hacía el
 * prototipo— borra la diferencia que da sentido al informe.
 */
import { useState } from 'react'
import { Button, Card, CardContent, CardHeader, CardTitle, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, StatusBadge } from '@mpfn/ui'
import { DataTable, EmptyState, FilterBar, SearchBar, type DataTableColumn } from '@mpfn/patterns'
import { ShieldCheck } from 'lucide-react'
import { useDerivaciones, useDerivacionesPendientes } from '../api/consultas.js'
import { etiquetaDe, useCatalogos } from '../api/catalogos.js'
import type { CausaDerivacion, DerivacionOci, DerivacionPendiente } from '../api/tipos.js'
import { Datos } from '../componentes/Datos.js'
import { Pagina } from '../componentes/Pagina.js'
import { DialogoDerivacion } from '../componentes/DialogoDerivacion.js'
import { irA } from '../navegacion/Enlace.js'
import { usePuede } from '../sesion/contexto.js'
import { dni as formatearDni, fecha } from '../lib/formato.js'

const TODAS = 'todas'

/** `NEG-13`: la causa decide el tono. Es la distinción que el OCI necesita. */
const TONO_DE_CAUSA: Readonly<Record<CausaDerivacion, 'critical' | 'serious'>> = {
  documento_falso: 'critical',
  no_acreditado: 'serious',
}

export function DerivacionesOci() {
  const catalogos = useCatalogos()
  const pendientes = useDerivacionesPendientes()
  const puedeDerivar = usePuede('oci.derivar')

  const [causa, setCausa] = useState<string>(TODAS)
  const [busqueda, setBusqueda] = useState('')
  const derivadas = useDerivaciones(causa === TODAS ? undefined : causa)

  const [enDerivacion, setEnDerivacion] = useState<DerivacionPendiente | undefined>(undefined)

  const columnasPendientes: ReadonlyArray<DataTableColumn<DerivacionPendiente>> = [
    { id: 'documento', header: 'Documento', cell: (fila) => fila.denominacion },
    {
      id: 'trabajador',
      header: 'Trabajador',
      cell: (fila) => `${fila.nombres} · ${formatearDni(fila.dni)}`,
      hideOnMobile: true,
    },
    {
      id: 'causa',
      header: 'Causa',
      cell: (fila) => (
        <StatusBadge
          tone={TONO_DE_CAUSA[fila.causa]}
          label={etiquetaDe(catalogos.causasDerivacion, fila.causa)}
        />
      ),
    },
    {
      id: 'accion',
      header: '',
      align: 'right',
      cell: (fila) =>
        puedeDerivar ? (
          <Button
            variant="destructive"
            size="sm"
            onClick={(evento) => {
              evento.stopPropagation()
              setEnDerivacion(fila)
            }}
          >
            Derivar
          </Button>
        ) : null,
    },
  ]

  const columnasDerivadas: ReadonlyArray<DataTableColumn<DerivacionOci>> = [
    { id: 'informe', header: 'Informe', cell: (fila) => fila.numero_informe },
    { id: 'documento', header: 'Documento', cell: (fila) => fila.denominacion },
    {
      id: 'trabajador',
      header: 'Trabajador',
      cell: (fila) => `${fila.nombres} · ${formatearDni(fila.dni)}`,
      hideOnMobile: true,
    },
    {
      id: 'causa',
      header: 'Causa',
      cell: (fila) => (
        <StatusBadge
          tone={TONO_DE_CAUSA[fila.causa]}
          label={etiquetaDe(catalogos.causasDerivacion, fila.causa)}
        />
      ),
    },
    { id: 'fecha', header: 'Fecha', cell: (fila) => fecha(fila.fecha), hideOnMobile: true },
  ]

  return (
    <Pagina
      titulo="Derivaciones al Órgano de Control Interno"
      descripcion="Un documento se deriva cuando una entidad negó su autenticidad, o cuando se agotaron los requerimientos sin respuesta."
    >

      <Card>
        <CardHeader>
          <CardTitle>Pendientes de derivar</CardTitle>
        </CardHeader>
        <CardContent>
          <Datos estado={pendientes}>
            {(lista) =>
              lista.length === 0 ? (
                <EmptyState
                  icon={ShieldCheck}
                  title="No hay documentos pendientes de derivar"
                  description="Aparecerán aquí en cuanto un requerimiento cierre como irregular o no acreditado."
                />
              ) : (
                <DataTable
                  columns={columnasPendientes}
                  rows={lista}
                  rowKey={(fila) => fila.id}
                  onRowClick={(fila) => {
                    irA('documento', { id: fila.id })
                  }}
                  rowActionLabel={(fila) => `Abrir ${fila.denominacion}`}
                  empty={{ title: 'Sin pendientes' }}
                />
              )
            }
          </Datos>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Derivadas</CardTitle>
        </CardHeader>
        <CardContent>
          <Datos estado={derivadas}>
            {(lista) => {
              const texto = busqueda.trim().toLowerCase()
              const filtradas = texto
                ? lista.filter((fila) =>
                    `${fila.numero_informe} ${fila.denominacion} ${fila.nombres} ${fila.dni}`
                      .toLowerCase()
                      .includes(texto),
                  )
                : lista
              return (
                <DataTable
                  columns={columnasDerivadas}
                  rows={filtradas}
                  rowKey={(fila) => fila.id}
                  toolbar={
                    <FilterBar
                      primary={
                        <SearchBar
                          value={busqueda}
                          onChange={setBusqueda}
                          label="Buscar derivación"
                          placeholder="Informe, documento o trabajador"
                        />
                      }
                      filters={
                        <Select value={causa} onValueChange={setCausa}>
                          <SelectTrigger aria-label="Filtrar por causa">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={TODAS}>Todas las causas</SelectItem>
                            {catalogos.causasDerivacion.map((opcion) => (
                              <SelectItem key={opcion.codigo} value={opcion.codigo}>
                                {opcion.etiqueta}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      }
                    />
                  }
                  empty={{ title: 'Todavía no se ha derivado ningún documento' }}
                />
              )
            }}
          </Datos>
        </CardContent>
      </Card>

      {enDerivacion && (
        <DialogoDerivacion
          documentoId={enDerivacion.id}
          denominacion={enDerivacion.denominacion}
          causa={enDerivacion.causa}
          abierto
          onCambio={(abierto) => {
            if (!abierto) setEnDerivacion(undefined)
          }}
        />
      )}
    </Pagina>
  )
}
